import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Lang = 'ko' | 'en';

const STORAGE_KEY = 'cloudbtl_lang';

function initialLang(): Lang {
  // English-first: 기본은 영어. 사용자가 명시적으로 고른 언어(localStorage)만 존중하고,
  // 브라우저 로케일로 한국어를 자동선택하지 않는다(글로벌 제품 기본값 = en). 토글로 한국어 가능.
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ko' || saved === 'en') return saved;
  } catch {
    /* ignore */
  }
  return 'en';
}

interface I18nState {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

/**
 * Default context value so the hooks work even without a provider (e.g. in
 * isolated component tests). The real <I18nProvider> overrides this with
 * stateful, persisted language selection.
 */
const Ctx = createContext<I18nState>({ lang: initialLang(), setLang: () => undefined });

export function I18nProvider({
  children,
  lang: initial,
}: {
  children: ReactNode;
  /** 초기 언어 강제(주로 테스트). 미지정이면 저장값/기본(en)을 따른다. */
  lang?: Lang;
}): JSX.Element {
  const [lang, setLangState] = useState<Lang>(initial ?? initialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch {
      /* ignore */
    }
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nState {
  return useContext(Ctx);
}

/** Pick the current language's value from a { ko, en } pair. */
export function useT(): <T>(dict: { ko: T; en: T }) => T {
  const { lang } = useI18n();
  return useCallback(<T,>(dict: { ko: T; en: T }): T => dict[lang], [lang]);
}
